import React, { useEffect, useState } from 'react';
import Container from '@/components/ui/container';
import InputUi from '@/components/ui/inputui';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import Layout from 'src/layout';
import TableSkeleton from '@/components/ui/tableSkeleton';
import { getAllFeaturedCoupons, createFeaturedCoupon, updateFeaturedCoupon, deleteFeaturedCoupon } from '../../lib/api/featuredCouponsApi';
import { getFilteredCoupons } from '../../lib/api/couponsApi';
import { toast } from 'react-toastify';
import {
    RiStarLine,
    RiAddLine,
    RiDeleteBinLine,
    RiRefreshLine,
    RiCheckLine,
    RiCloseLine,
    RiCouponLine
} from 'react-icons/ri';

const FeaturedCoupons = () => {
    const [featuredCoupons, setFeaturedCoupons] = useState([]);
    const [allCoupons, setAllCoupons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);

    // Form state
    const [selectedCouponCode, setSelectedCouponCode] = useState('');
    const [customCouponCode, setCustomCouponCode] = useState('');
    const [popupImage, setPopupImage] = useState('');

    const fetchFeatured = async () => {
        setLoading(true);
        try {
            const res = await getAllFeaturedCoupons();
            if (res.success) {
                setFeaturedCoupons(res.data || []);
            } else {
                toast.error(res.error || 'Failed to fetch featured coupons');
            }
        } catch (err) {
            console.error('Error fetching featured coupons:', err);
            toast.error('Failed to load featured coupons');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllCoupons = async () => {
        try {
            const res = await getFilteredCoupons({ page: 1, limit: 100 });
            if (res.success) {
                setAllCoupons(res.data || []);
            }
        } catch (err) {
            console.error('Error fetching system coupons:', err);
        }
    };

    useEffect(() => {
        fetchFeatured();
        fetchAllCoupons();
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        const codeToUse = selectedCouponCode === 'custom' ? customCouponCode.trim() : selectedCouponCode;

        if (!codeToUse) {
            toast.error('Please select or enter a coupon code');
            return;
        }

        setAdding(true);
        try {
            const res = await createFeaturedCoupon({
                couponCode: codeToUse,
                popupImage: popupImage.trim()
            });

            if (res.success) {
                toast.success('Featured coupon added successfully!');
                setSelectedCouponCode('');
                setCustomCouponCode('');
                setPopupImage('');
                fetchFeatured();
            } else {
                toast.error(res.error || 'Failed to add featured coupon');
            }
        } catch (err) {
            toast.error('Error adding featured coupon');
        } finally {
            setAdding(false);
        }
    };

    const handleToggleActive = async (item) => {
        try {
            const updatedStatus = !item.isActive;
            const res = await updateFeaturedCoupon(item.id, { isActive: updatedStatus });
            if (res.success) {
                toast.success(`Coupon ${item.couponCode} is now ${updatedStatus ? 'Active' : 'Inactive'}`);
                fetchFeatured();
            } else {
                toast.error(res.error || 'Failed to update status');
            }
        } catch (err) {
            toast.error('Error updating coupon status');
        }
    };

    const handleDelete = async (id, code) => {
        if (!window.confirm(`Are you sure you want to remove "${code}" from floating coupons?`)) return;

        try {
            const res = await deleteFeaturedCoupon(id);
            if (res.success) {
                toast.success('Featured coupon deleted');
                fetchFeatured();
            } else {
                toast.error(res.error || 'Failed to delete featured coupon');
            }
        } catch (err) {
            toast.error('Error deleting featured coupon');
        }
    };

    return (
        <Layout active="admin-coupons-featured" title="Featured Coupons">
            <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
                <Container>
                    {/* Header */}
                    <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                <RiStarLine className="text-amber-500" /> Floating & Featured Homepage Coupons
                            </h1>
                            <p className="text-slate-500 text-sm mt-1">
                                Manage coupons displayed in the floating popup modal on the homepage bottom-right.
                            </p>
                        </div>
                        <Button
                            onClick={fetchFeatured}
                            disabled={loading}
                            variant="outline"
                            className="bg-white hover:bg-slate-100 text-slate-700 border-slate-200 shadow-sm"
                        >
                            <RiRefreshLine className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                        </Button>
                    </div>

                    {/* Add Featured Coupon Form */}
                    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4 mb-6">
                        <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2 pb-2 border-b border-slate-100">
                            <RiAddLine className="text-amber-500" /> Add Coupon to Floating Modal
                        </h2>

                        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                    Select Coupon Code *
                                </label>
                                <select
                                    value={selectedCouponCode}
                                    onChange={(e) => setSelectedCouponCode(e.target.value)}
                                    className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                                >
                                    <option value="">-- Choose Existing Coupon --</option>
                                    {allCoupons.map((c) => (
                                        <option key={c.couponID || c.couponCode} value={c.couponCode}>
                                            {c.couponCode} ({c.discountType === 'percentage' ? `${c.discountValue}% OFF` : `₹${c.discountValue} OFF`})
                                        </option>
                                    ))}
                                    <option value="custom">+ Type Custom Code</option>
                                </select>
                            </div>

                            {selectedCouponCode === 'custom' && (
                                <div>
                                    <InputUi
                                        label="Custom Coupon Code"
                                        placeholder="e.g. WELCOME50"
                                        value={customCouponCode}
                                        datafunction={(e) => setCustomCouponCode(e.target.value)}
                                    />
                                </div>
                            )}

                            <div>
                                <InputUi
                                    label="Banner / Popup Image URL (Optional)"
                                    placeholder="https://example.com/banner.jpg"
                                    value={popupImage}
                                    datafunction={(e) => setPopupImage(e.target.value)}
                                />
                            </div>

                            <div>
                                <Button
                                    type="submit"
                                    disabled={adding || !selectedCouponCode}
                                    className="w-full bg-amber-600 hover:bg-amber-700 text-white font-medium shadow-sm h-10 flex items-center justify-center gap-2"
                                >
                                    <RiAddLine className="w-4 h-4" /> {adding ? 'Adding...' : 'Add Featured Coupon'}
                                </Button>
                            </div>
                        </form>
                    </div>

                    {/* Coupons Table */}
                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-base font-semibold text-slate-800">Featured Homepage Coupons</h3>
                            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                                {featuredCoupons.length} Active Rules
                            </span>
                        </div>

                        <Table>
                            <TableHeader>
                                <TableRow className="bg-slate-50/80 border-b border-slate-200">
                                    <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Coupon Code</TableHead>
                                    <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Discount</TableHead>
                                    <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Min. Spend</TableHead>
                                    <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Status</TableHead>
                                    <TableHead className="px-6 py-3.5 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody className="divide-y divide-slate-100">
                                {loading ? (
                                    <TableSkeleton columns={5} rows={4} />
                                ) : featuredCoupons.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-10 text-slate-500 text-sm">
                                            No featured coupons set. Add one above to display on homepage modal!
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    featuredCoupons.map((item) => (
                                        <TableRow key={item.id} className="hover:bg-slate-50/80 transition-colors">
                                            <TableCell className="px-6 py-4 font-bold text-slate-900 flex items-center gap-2">
                                                <RiCouponLine className="text-amber-500" /> {item.couponCode}
                                            </TableCell>
                                            <TableCell className="px-6 py-4">
                                                {item.discountValue ? (
                                                    <span className="bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1 rounded-full text-xs font-semibold">
                                                        {item.discountType === 'percentage' || item.discountType === 'percent' ? `${item.discountValue}% OFF` : `₹${item.discountValue} OFF`}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-400 text-xs">Standard</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="px-6 py-4 text-slate-600 text-sm">
                                                {item.minOrderValue ? `₹${item.minOrderValue}` : 'No Min'}
                                            </TableCell>
                                            <TableCell className="px-6 py-4 text-center">
                                                <button
                                                    onClick={() => handleToggleActive(item)}
                                                    className={`px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${
                                                        item.isActive
                                                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                                                            : 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100'
                                                    }`}
                                                >
                                                    {item.isActive ? <RiCheckLine /> : <RiCloseLine />}
                                                    {item.isActive ? 'Active' : 'Inactive'}
                                                </button>
                                            </TableCell>
                                            <TableCell className="px-6 py-4 text-right">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleDelete(item.id, item.couponCode)}
                                                    className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                                >
                                                    <RiDeleteBinLine className="w-4 h-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </Container>
            </div>
        </Layout>
    );
};

export default FeaturedCoupons;
